import { useCallback, useState } from 'react';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { hasVisualPhase } from '../../lib/pipeline';
import type {
  DraftVariantRead,
  GenerationRequestDetailRead,
  GenerationRequestRead,
  QuestionAnswer,
} from '../../types/api';
import { selectVariant } from '../generation/generationApi';
import { LineagePanel } from './LineagePanel';
import { RejectionCapPanel } from './RejectionCapPanel';
import { ReviewBudget } from './ReviewBudget';
import { ShapedByLine } from './ShapedByLine';
import { useRejectRequest } from './useRejectRequest';
import { withRequestFields } from './useReviseVariant';
import { VariantCard } from './VariantCard';

interface CopySelectionProps {
  businessId: string;
  request: GenerationRequestDetailRead;
  /** Server response for the selection, or the request with one variant rewritten. */
  onUpdated: (next: GenerationRequestDetailRead) => void;
  /** The request moved on without us; reload it. */
  onStale: () => void;
}

/** Message for the domain errors selection can provoke. */
function selectionMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'QUESTIONS_UNANSWERED':
        return 'Answer every clarifying question on that version before choosing it.';
      case 'REQUEST_NOT_AWAITING_REVIEW':
        return 'This post has already moved past this step. Reloading it now.';
      case 'VARIANT_NOT_FOUND':
        return 'That version is no longer here. Reload the post and pick again.';
      default:
        return caught.message;
    }
  }
  return getErrorMessage(caught, 'That choice did not save. Try again.');
}

/**
 * Review Step 1 — copy selection (SRS §3 stage 4).
 *
 * Two variants side by side, each carrying its own advisory verdicts and its
 * own clarifying questions. On a post that carries the user's own picture, or a
 * text-only post (the caption is the whole post), this is the only review step:
 * choosing here completes the request. On anything we
 * render — a carousel, or a single image we were asked to draw — choosing hands
 * the winning caption to the design agent and the run continues.
 *
 * Not this? That is **Send it back** on the card, and it is one control rather
 * than the two this screen used to carry: the dialog asks what kind of problem
 * it is and spends accordingly — a wording complaint rewrites that one caption
 * on the reviewer's note and leaves the other card alone, while a wrong angle
 * is a real regeneration that opens a new request and spends one of the
 * lineage's three attempts. Rewriting a caption by hand is still the free way
 * and spends neither.
 *
 * What this lineage has already spent, and what it already tried, are both on
 * screen: the budget line under the cards, and the earlier attempts above them.
 */
export function CopySelection({ businessId, request, onUpdated, onStale }: CopySelectionProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One per screen: the cap it holds is the request's, and a rejection navigates
  // away from this screen rather than changing a card.
  const rejection = useRejectRequest(businessId, request.id, request.attempt);

  const willRender = hasVisualPhase(request);

  const variants = [...request.variants].sort(
    (left, right) => left.variant_index - right.variant_index,
  );

  async function handleSelect(variantId: string, questionAnswers: QuestionAnswer[]) {
    if (isSubmitting) return;

    setError(null);
    setIsSubmitting(true);
    try {
      const updated = await selectVariant(businessId, request.id, {
        variant_id: variantId,
        question_answers: questionAnswers,
      });
      onUpdated(updated);
    } catch (caught) {
      setError(selectionMessage(caught));
      setIsSubmitting(false);
      if (caught instanceof ApiError && caught.code === 'REQUEST_NOT_AWAITING_REVIEW') {
        onStale();
      }
    }
  }

  /** A rewritten caption replaces its own variant; the other card is untouched. */
  const applyVariant = useCallback(
    (saved: DraftVariantRead) => {
      onUpdated({
        ...request,
        variants: request.variants.map((variant) => (variant.id === saved.id ? saved : variant)),
      });
    },
    [onUpdated, request],
  );

  /**
   * A revision hands back the request itself, now `revising`. It goes through
   * the same path as a selection, so the page swaps to the stage tracker and
   * the stream resubscribes on a fresh token.
   */
  const applySentBack = useCallback(
    (next: GenerationRequestRead) => onUpdated(withRequestFields(request, next)),
    [onUpdated, request],
  );

  if (variants.length === 0) {
    return (
      <p className="form__notice">
        This post is waiting for your review but has no versions added yet. Reload, or create a new
        post.
      </p>
    );
  }

  const isBusy = isSubmitting || rejection.isSubmitting;

  return (
    <section className="copy-selection">
      <header className="copy-selection__head">
        <h2 className="copy-selection__title">Two versions</h2>
        <p className="copy-selection__lede">
          Pick one. The checks under each caption are advice; you choose which one goes out.
          {willRender
            ? ' Only the one you pick gets drawn.'
            : request.post_type === 'text_only'
              ? ' Text only: the caption you pick is the whole post, and picking it finishes it.'
              : null}
        </p>
      </header>

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      <LineagePanel lineage={request.lineage} attempt={request.attempt} />

      {rejection.showCapPanel ? (
        <RejectionCapPanel step="copy" isBusy={isBusy} onAcceptBest={rejection.dismissCap} />
      ) : null}

      <div className="variant-grid">
        {variants.map((variant) => (
          <VariantCard
            key={variant.id}
            businessId={businessId}
            request={request}
            variant={variant}
            isSubmitting={isBusy}
            rejection={rejection}
            onSelect={(variantId, questionAnswers) => void handleSelect(variantId, questionAnswers)}
            onCaptionSaved={applyVariant}
            onSentBack={applySentBack}
            onStale={onStale}
          />
        ))}
      </div>

      <ShapedByLine request={request} />

      <div className="copy-selection__footer">
        <ReviewBudget attempt={request.attempt} revisions={request.revisions} />
        <p className="copy-selection__reject-note">
          Neither right? <strong>Send it back</strong> on the closer card. Only a wrong angle costs
          a full regeneration.
        </p>
      </div>
    </section>
  );
}
